import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import enCommon from "./locales/en/common.json";
import enAuth from "./locales/en/auth.json";
import enAccount from "./locales/en/account.json";
import enFarms from "./locales/en/farms.json";
import enImagery from "./locales/en/imagery.json";
import enIndices from "./locales/en/indices.json";
import enWeather from "./locales/en/weather.json";
import enWeatherIndices from "./locales/en/weatherIndices.json";
import enWeatherRisk from "./locales/en/weatherRisk.json";
import enActionCenter from "./locales/en/actionCenter.json";
import enRecommendations from "./locales/en/recommendations.json";
import enSignals from "./locales/en/signals.json";
import enInsights from "./locales/en/insights.json";
import enAlerts from "./locales/en/alerts.json";
import enRules from "./locales/en/rules.json";
import enImageryWeatherConfig from "./locales/en/imageryWeatherConfig.json";
import enUsers from "./locales/en/users.json";
import enAdmin from "./locales/en/admin.json";
import enDecisionTrees from "./locales/en/decisionTrees.json";
import enDecisionTreesBeta from "./locales/en/decisionTreesBeta.json";
import enDecisionTreesEstate from "./locales/en/decisionTreesEstate.json";
import enSettings from "./locales/en/settings.json";
import enIntegrationsHealth from "./locales/en/integrationsHealth.json";
import enIntegrations from "./locales/en/integrations.json";
import enFieldAccess from "./locales/en/fieldAccess.json";
import enResources from "./locales/en/resources.json";
import enBoard from "./locales/en/board.json";
import enReports from "./locales/en/reports.json";
import enFarmConsole from "./locales/en/farmConsole.json";
import enPlanTemplates from "./locales/en/planTemplates.json";
import enBulkUpdates from "./locales/en/bulkUpdates.json";
import enFarmHealth from "./locales/en/farmHealth.json";
import enTimeline from "./locales/en/timeline.json";
import enInvestors from "./locales/en/investors.json";
import arCommon from "./locales/ar/common.json";
import arAuth from "./locales/ar/auth.json";
import arAccount from "./locales/ar/account.json";
import arFarms from "./locales/ar/farms.json";
import arImagery from "./locales/ar/imagery.json";
import arIndices from "./locales/ar/indices.json";
import arWeather from "./locales/ar/weather.json";
import arWeatherIndices from "./locales/ar/weatherIndices.json";
import arWeatherRisk from "./locales/ar/weatherRisk.json";
import arActionCenter from "./locales/ar/actionCenter.json";
import arRecommendations from "./locales/ar/recommendations.json";
import arSignals from "./locales/ar/signals.json";
import arInsights from "./locales/ar/insights.json";
import arAlerts from "./locales/ar/alerts.json";
import arRules from "./locales/ar/rules.json";
import arImageryWeatherConfig from "./locales/ar/imageryWeatherConfig.json";
import arUsers from "./locales/ar/users.json";
import arAdmin from "./locales/ar/admin.json";
import arDecisionTrees from "./locales/ar/decisionTrees.json";
import arDecisionTreesBeta from "./locales/ar/decisionTreesBeta.json";
import arDecisionTreesEstate from "./locales/ar/decisionTreesEstate.json";
import arSettings from "./locales/ar/settings.json";
import arIntegrationsHealth from "./locales/ar/integrationsHealth.json";
import arIntegrations from "./locales/ar/integrations.json";
import arFieldAccess from "./locales/ar/fieldAccess.json";
import arResources from "./locales/ar/resources.json";
import arBoard from "./locales/ar/board.json";
import arReports from "./locales/ar/reports.json";
import arFarmConsole from "./locales/ar/farmConsole.json";
import arPlanTemplates from "./locales/ar/planTemplates.json";
import arBulkUpdates from "./locales/ar/bulkUpdates.json";
import arFarmHealth from "./locales/ar/farmHealth.json";
import arTimeline from "./locales/ar/timeline.json";
import arInvestors from "./locales/ar/investors.json";

export type SupportedLanguage = "en" | "ar";

export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = ["en", "ar"];
export const DEFAULT_LANGUAGE: SupportedLanguage = "en";

// Per ARCHITECTURE.md Â§ 11, the frontend ships with two locales (en, ar)
// loaded eagerly at this scope (the bundle is small). Future modules add
// their own namespaces lazily via i18next-http-backend.
const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    account: enAccount,
    farms: enFarms,
    imagery: enImagery,
    indices: enIndices,
    weather: enWeather,
    weatherIndices: enWeatherIndices,
    weatherRisk: enWeatherRisk,
    actionCenter: enActionCenter,
    recommendations: enRecommendations,
    signals: enSignals,
    insights: enInsights,
    alerts: enAlerts,
    rules: enRules,
    imageryWeatherConfig: enImageryWeatherConfig,
    admin: enAdmin,
    decisionTrees: enDecisionTrees,
    decisionTreesBeta: enDecisionTreesBeta,
    decisionTreesEstate: enDecisionTreesEstate,
    users: enUsers,
    settings: enSettings,
    integrationsHealth: enIntegrationsHealth,
    integrations: enIntegrations,
    fieldAccess: enFieldAccess,
    resources: enResources,
    board: enBoard,
    reports: enReports,
    farmConsole: enFarmConsole,
    planTemplates: enPlanTemplates,
    bulkUpdates: enBulkUpdates,
    farmHealth: enFarmHealth,
    timeline: enTimeline,
    investors: enInvestors,
  },
  ar: {
    common: arCommon,
    auth: arAuth,
    account: arAccount,
    farms: arFarms,
    imagery: arImagery,
    indices: arIndices,
    weather: arWeather,
    weatherIndices: arWeatherIndices,
    weatherRisk: arWeatherRisk,
    actionCenter: arActionCenter,
    recommendations: arRecommendations,
    signals: arSignals,
    insights: arInsights,
    alerts: arAlerts,
    rules: arRules,
    imageryWeatherConfig: arImageryWeatherConfig,
    admin: arAdmin,
    decisionTrees: arDecisionTrees,
    decisionTreesBeta: arDecisionTreesBeta,
    decisionTreesEstate: arDecisionTreesEstate,
    users: arUsers,
    settings: arSettings,
    integrationsHealth: arIntegrationsHealth,
    integrations: arIntegrations,
    fieldAccess: arFieldAccess,
    resources: arResources,
    board: arBoard,
    reports: arReports,
    farmConsole: arFarmConsole,
    planTemplates: arPlanTemplates,
    bulkUpdates: arBulkUpdates,
    farmHealth: arFarmHealth,
    timeline: arTimeline,
    investors: arInvestors,
  },
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    defaultNS: "common",
    ns: [
      "common",
      "auth",
      "account",
      "farms",
      "imagery",
      "indices",
      "weather",
      "weatherIndices",
      "weatherRisk",
      "actionCenter",
      "recommendations",
      "signals",
      "insights",
      "alerts",
      "rules",
      "imageryWeatherConfig",
      "admin",
      "decisionTrees",
      "decisionTreesBeta",
      "decisionTreesEstate",
      "users",
      "settings",
      "integrationsHealth",
      "integrations",
      "resources",
      "board",
      "reports",
      "farmConsole",
      "planTemplates",
      "bulkUpdates",
      "timeline",
      "investors",
    ],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: "agripulse.lang",
    },
    react: { useSuspense: false },
  });

/**
 * Apply the current language to <html lang> and <html dir>. Called at
 * startup and on every language change. RTL is inferred from the locale
 * rather than hard-coded so additional RTL languages slot in cleanly.
 */
function syncHtmlAttributes(language: string): void {
  const root = document.documentElement;
  root.setAttribute("lang", language);
  root.setAttribute("dir", language === "ar" ? "rtl" : "ltr");
}

i18n.on("languageChanged", syncHtmlAttributes);
syncHtmlAttributes(i18n.language || DEFAULT_LANGUAGE);

export default i18n;
